import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { parseISO } from "date-fns";
import { Card, DatePicker, Input, TextArea } from "@jhool-io/fe-components";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
    ClientFeeValues,
    IClientFee,
    IClientFeeValue,
    IEditClientFee,
} from "../../../types/clients.types";
import { calculateFees } from "../../../../../utils/helpers/fees/fees";
import {
    handleFormatDatePickerValue,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../../utils/helpers";
import { useEditClientCurrentFee } from "../../../hooks/clients.mutations";
import useToast from "../../../../../hooks/useToast";
import { useFetchPracticeSettings } from "../../../../practice-settings/hooks/queries/practice-settings";

interface EditClientFeeFormProps {
    fee: IClientFee;
    onFormSubmit(): void;
    insuranceId?: string;
}

const feeGroupings: Record<string, ClientFeeValues[]> = {
    "Drs fee": [
        ClientFeeValues.DR_FEE,
        ClientFeeValues.DR_FEE_A,
        ClientFeeValues.DR_FEE_B,
        ClientFeeValues.DR_FEE_C,
    ],
    "Lic fee": [
        ClientFeeValues.LIC_FEE,
        ClientFeeValues.LIC_A_FEE,
        ClientFeeValues.LIC_B_FEE,
        ClientFeeValues.LIC_C_FEE,
    ],
    "Other fees": [
        ClientFeeValues.MASTERS_FEE,
        ClientFeeValues.INTERN_FEE,
        ClientFeeValues.PSYCH_FEE,
        ClientFeeValues.TESTING_FEE,
        ClientFeeValues.GIFTED_ASSESSMENT_FEE,
        ClientFeeValues.SHORT_TERM_EVALUATION_DOCUMENTATION_FEE,
        ClientFeeValues.INN_MASTERS_FEE,
    ],
};

const desiredFeeValues = Object.values(feeGroupings).flat();

const schema = yup.object({
    cal_year: yup.date().required("Calender Year is required"),
    start_date: yup.date().required("Start Date is required"),
    end_date: yup
        .date()
        .required("End Date is required")
        .min(
            yup.ref("start_date"),
            "The end date cannot be earlier than the start date"
        ),
    reason_for_update: yup.string().required("Reason for update is required"),
    fee_values: yup.array().of(
        yup.object({
            type: yup.string().required(),
            value: yup.string().test({
                test: (val) =>
                    val ? Boolean(calculateFees(val as string)) : true,
                message: "Invalid fee format",
            }),
        })
    ),
});

export default function EditClientFeeForm({
    fee,
    onFormSubmit,
    insuranceId,
}: EditClientFeeFormProps) {
    const [startDate, setStartDate] = React.useState<Date | null>(
        parseISO(fee.start_date)
    );
    const [endDate, setEndDate] = React.useState<Date | null>(
        parseISO(fee.end_date)
    );

    const { clientId } = useParams();

    const { mutate } = useEditClientCurrentFee(clientId || "", fee.fee_id);

    const queryClient = useQueryClient();

    const { toast } = useToast();

    const { data: practiceFeeSettings } = useFetchPracticeSettings({
        setting_name: "fee_type",
        category: "fees",
    });

    const practiceHasINNFee =
        practiceFeeSettings &&
        practiceFeeSettings?.data.settings.length > 0 &&
        practiceFeeSettings?.data.settings[0].current_value === "INN";

    const visibleFeeTypes = practiceHasINNFee
        ? desiredFeeValues
        : desiredFeeValues.filter((t) => t !== "inn masters fee");

    const providerFeeValues = fee.fee_values.filter(
        (f) => f.provider_id || f.provider?.provider_id
    );

    const providerFeeStartIndex = visibleFeeTypes.length;

    // Group provider-specific fees by provider so we can render them under each provider's name
    const groups: Record<
        string,
        {
            provider: {
                provider_id: string;
                first_name: string;
                last_name: string;
                credential: string;
            };
            fees: IClientFeeValue[];
        }
    > = {};

    providerFeeValues.forEach((f) => {
        const pid = f.provider_id || f.provider?.provider_id;
        if (!pid || !f.provider) return;

        if (groups[pid]) {
            groups[pid].fees.push(f);
        } else {
            groups[pid] = { provider: f.provider, fees: [f] };
        }
    });

    const providerFeesByProvider = Object.values(groups);

    const getExistingValue = (type: ClientFeeValues): string => {
        return fee.fee_values.find((v) => v.type === type)?.value || "";
    };

    const defaultFeeValues = [
        ...visibleFeeTypes.map((type) => ({
            type,
            value: getExistingValue(type),
        })),
        ...providerFeeValues.map((f) => ({
            type: f.type,
            value: f.value,
            provider_id: f.provider_id || f.provider?.provider_id,
        })),
    ];

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<IEditClientFee>({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: {
            ...fee,
            fee_values: defaultFeeValues,
        },
    });

    const onSubmit = (payload: IEditClientFee) => {
        const cleanedFeeValues = (payload.fee_values || [])
            .filter((fv) => fv.value && fv.value.trim() !== "")
            .map((fv) => ({
                type: fv.type,
                value: fv.value,
                ...(fv.provider_id && { provider_id: fv.provider_id }),
            }));

        const dataToSend: IEditClientFee = {
            cal_year: payload.cal_year,
            start_date: handleFormatDatePickerValue(
                payload.start_date as string
            ),
            self_pay: payload.self_pay,
            accepts_assignment: payload.accepts_assignment,
            alt_email_sent: payload.alt_email_sent,
            end_date: handleFormatDatePickerValue(payload.end_date as string),
            reason_for_update: payload.reason_for_update,
            fee_values: cleanedFeeValues,
            insurance_id: insuranceId || undefined,
        };

        if (fee.cal_year !== payload.cal_year)
            dataToSend.cal_year = new Date(payload.cal_year).getFullYear();

        mutate(dataToSend, {
            onSuccess: (res) => {
                if (clientId) {
                    queryClient.invalidateQueries({
                        queryKey: [clientId, "fees"],
                    });
                    queryClient.invalidateQueries({
                        queryKey: [clientId, `insurance-fees`],
                    });
                }
                toast({
                    mode: "success",
                    message: res.message || "Client fee updated successfully",
                });
                onFormSubmit();
            },
            onError: (error) => {
                toast({
                    mode: "error",
                    message:
                        error.response?.data.message ||
                        "Could not update client fee at this time",
                });
            },
        });
    };

    return (
        <form id="edit-fees" onSubmit={handleSubmit(onSubmit)}>
            <div className="p-12 [border-bottom:1px_solid_var(--color-stroke)]">
                <Card classNames="grid grid-cols-[repeat(auto-fit,minmax(70px,1fr))] bg-[--card-bg-gray] gap-2">
                    <div className="flex flex-col items-start gap-y-3 text-xs font-medium text-caption">
                        <span className="uppercase text-textSec leading-normal">
                            oonded
                        </span>
                        <span>{fee.oonded || "n/a"}</span>
                    </div>
                    <div className="flex flex-col items-start gap-y-3 text-xs font-medium">
                        <span className="uppercase text-textSec leading-normal">
                            oonco
                        </span>
                        <span>{fee.oonco || "n/a"}</span>
                    </div>
                    <div className="flex flex-col items-start gap-y-3 text-xs font-medium">
                        <span className="uppercase text-textSec leading-normal">
                            oonoop
                        </span>
                        <span>{fee.oonoop || "n/a"}</span>
                    </div>
                    <div className="flex flex-col items-start gap-y-3 text-xs font-medium">
                        <span className="uppercase text-textSec leading-normal">
                            ooncoins
                        </span>
                        <span>{fee.ooncoins || "n/a"}</span>
                    </div>
                    <div className="flex flex-col items-start gap-y-3 text-xs font-medium">
                        <span className="uppercase text-textSec leading-normal">
                            innded
                        </span>
                        <span>{fee.innded || "n/a"}</span>
                    </div>
                    <div className="flex flex-col items-start gap-y-3 text-xs font-medium">
                        <span className="uppercase text-textSec leading-normal">
                            innco
                        </span>
                        <span>{fee.innco || "n/a"}</span>
                    </div>
                    <div className="flex flex-col items-start gap-y-3 text-xs font-medium">
                        <span className="uppercase text-textSec leading-normal">
                            innoop
                        </span>
                        <span>{fee.innoop || "n/a"}</span>
                    </div>
                    <div className="flex flex-col items-start gap-y-3 text-xs font-medium">
                        <span className="uppercase text-textSec leading-normal">
                            inncoins
                        </span>
                        <span>{fee.inncoins || "n/a"}</span>
                    </div>
                    <div className="flex flex-col items-start gap-y-3 text-xs font-medium">
                        <span className="uppercase text-textSec leading-normal">
                            ucr
                        </span>
                        <span>{fee.ucr || "n/a"}</span>
                    </div>
                </Card>
            </div>
            <div className="px-[--padding-card-x] pt-12">
                <div className="fg">
                    <Input
                        {...register("cal_year")}
                        defaultValue={fee.cal_year}
                        label="Calender Year"
                        placeholder="Calender Year"
                        hasError={!!errors.cal_year}
                        errorText={
                            errors.cal_year?.message?.includes(
                                "cal_year must be a `date` type, but the final value was"
                            )
                                ? "Invalid date"
                                : errors.cal_year?.message
                        }
                    />
                </div>
                <div className="fg fg-space-between two flex">
                    <Controller
                        name="start_date"
                        control={control}
                        defaultValue={fee.start_date}
                        render={({ field }) => (
                            <DatePicker
                                label="Start Date"
                                onChange={(date) => {
                                    field.onChange(date);
                                    setStartDate(date);
                                }}
                                selected={startDate}
                                hasError={!!errors.start_date}
                                errorText={
                                    errors.start_date?.type === "typeError"
                                        ? "Invalid date value"
                                        : errors.start_date?.message
                                }
                            />
                        )}
                    />
                    <Controller
                        name="end_date"
                        control={control}
                        defaultValue={fee.end_date}
                        render={({ field }) => (
                            <DatePicker
                                label="End Date"
                                onChange={(date) => {
                                    field.onChange(date);
                                    setEndDate(date);
                                }}
                                selected={endDate}
                                hasError={!!errors.end_date}
                                errorText={
                                    errors.start_date?.type === "typeError"
                                        ? "Invalid date value"
                                        : errors.end_date?.message
                                }
                            />
                        )}
                    />
                </div>

                <div className="fg space-y-6">
                    {Object.entries(feeGroupings).map(
                        ([groupName, feeTypes]) => {
                            const groupFees = visibleFeeTypes
                                .map((type, index) => ({ type, index }))
                                .filter(({ type }) => feeTypes.includes(type));
                            if (groupFees.length === 0) return null;

                            return (
                                <div key={groupName}>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-6">
                                        {groupFees.map(({ type, index }) => {
                                            const fieldError =
                                                errors.fee_values?.[index]
                                                    ?.value;
                                            return (
                                                <Input
                                                    key={type}
                                                    {...register(
                                                        `fee_values.${index}.value` as const
                                                    )}
                                                    label={makeStringFirstLetterCapital(
                                                        removeEnumUnderscore(
                                                            type
                                                        )
                                                    )}
                                                    hasError={!!fieldError}
                                                    errorText={
                                                        fieldError?.message
                                                    }
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        }
                    )}
                </div>

                {providerFeesByProvider.length > 0 && (
                    <div className="mt-6">
                        <p className="text-xs font-semibold text-textSec uppercase mb-4">
                            Therapist-specific fees
                        </p>
                        {providerFeesByProvider.map(({ provider, fees }) => (
                            <div key={provider.provider_id} className="mb-4">
                                <p className="text-xs text-primary font-semibold mb-1">
                                    {provider.first_name} {provider.last_name}
                                    {provider.credential
                                        ? `, ${provider.credential}`
                                        : ""}
                                </p>
                                <div className="grid grid-cols">
                                    {fees.map((providerFee) => {
                                        const index =
                                            providerFeeStartIndex +
                                            providerFeeValues.indexOf(
                                                providerFee
                                            );
                                        const fieldError =
                                            errors.fee_values?.[index]?.value;
                                        return (
                                            <Input
                                                key={`${provider.provider_id}-${providerFee.type}`}
                                                {...register(
                                                    `fee_values.${index}.value` as const
                                                )}
                                                label={makeStringFirstLetterCapital(
                                                    removeEnumUnderscore(
                                                        providerFee.type
                                                    )
                                                )}
                                                hasError={!!fieldError}
                                                errorText={fieldError?.message}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div>
                    <TextArea
                        {...register("reason_for_update")}
                        defaultValue={fee.reason_for_update || ""}
                        label="Reason for update"
                        hasError={!!errors.reason_for_update}
                        errorText={errors.reason_for_update?.message}
                    />
                </div>
            </div>
        </form>
    );
}
